import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";

import { GenericTable, Input, toastMessage, Button, tCell, Dialog, ActionMenu, Dropdown, ConfirmationDialog } from "../utils/index";
import { buttonClasses } from "../utils/theme";
import { useCheckpoints, useOrgs, usePlant, useRoles, useRolesForFilter, useUserDetail, useUserPermissions } from "../../services/hooks";
import { useAddRoleMutation, useDeleteRoleMutation, useUpdateRoleMutation } from "../../api/userSlice";
import { Navigate } from "react-router-dom/dist";
import Genericfilter from "../utils/Genericfilter";
import { debounce, isObjectEqual } from "../../services/functions";
import { GenericstatsV2 } from "../utils/Genericstats";
import CommonTour, { TourIcon } from "../Tour/CommonTour";
import { Chip } from "@mui/material";

const rolesTourDummyData = [
	{
		_id: "6811cecd710dc5e3f1d4c795",
		role: "Bay Operator",
		displayName: "John Doe",
		addedBy: "65fbd96d6bc11b2703fac28d",
		addedOn: "2025-04-30T07:18:37.118Z",
		checkpoints: ["660a8c7701071b25bd87c3fd"],
		modifiedBy: "65fbd96d6bc11b2703fac28d",
		modifiedOn: "2025-04-30T07:18:37.118Z",
		orgId: "65fbd1a953312156626a627d",
		orgName: "Test seventh Company",
		__v: 0,
	},
];
const RolesTour = [
	{
		target: ".title",
		content: "Welcome to Role Management! Here you can define and manage user roles and permissions.",
		placement: "bottom",
	},
	{
		target: ".genericStats-containter",
		content: "These cards provide an overview of role statistics in your system.",
		placement: "bottom",
	},
	{
		target: "#Genericfilter",
		content: "Filter roles by name to find specific roles quickly.",
		placement: "bottom",
	},
	{
		target: ".a_tag_no",
		content: "Create new roles using this button.",
		placement: "left",
	},
	{
		target: ".main_content",
		content: "This table lists all available roles with their key information.",
		placement: "top",
	},
	{
		target: ".actionMenu",
		content: "Edit existing roles or modify their permissions using these action buttons.",
		placement: "left",
		condition: () => !!document.querySelector(".ActionMenu"),
	},
];
const defaultFilter = {
	pagesize: 10,
	page_no: 1,
};

const Roles = () => {
	const { userPermissions, isFetched } = useUserPermissions();
	const { userDetail, isSuperAdmin } = useUserDetail();
	const { orgOptions } = useOrgs({}, { skip: !isSuperAdmin });
	const { plantOptions, getPlantDetails, clearPlants } = usePlant(null, "rolePlants");
	const tourRef = useRef();
	const [isTourMode, setIsTourMode] = useState(false);
	const shouldRunTour = !userDetail?.moduleTour?.roles;
	const handleTourStart = () => {
		setIsTourMode(true);
		tourRef.current?.restartTour();
	};
	const shouldHandlePlant = isSuperAdmin || userPermissions?.managePlant?.value;
	useEffect(() => {
		if (userPermissions?.managePlant?.value && !isSuperAdmin) {
			getPlantDetails({});
		}
	}, [userDetail]);
	const [isModal, setIsModal] = useState(false);

	const [fetchStats, setFetchStats] = useState(false);

	const updateDataRef = useRef(null);

	const onModalOpen = () => setIsModal(true);
	const onModalClose = () => {
		setIsModal(false);
		if (updateDataRef.current) {
			updateDataRef.current = null;
		}
	};
	const filterRef = useRef(defaultFilter);
	const { page_no, pagesize } = filterRef.current;
	const { roles, count, isLoading, getRoles } = useRoles(filterRef.current);
	const {roleFilterOption, getRolesForFilter } =useRolesForFilter({})
	// console.log(roleFilterOption)
	const uniqueRoleFilterOption = Object.values(
		roleFilterOption.reduce((acc, item) => {
			if (!acc[item.label]) {
			acc[item.label] = item; // keep first occurrence
			}
			return acc;
		}, {})
	)


	const filterFields = [
		{
			inputType: "dropdown",
			id: "role",
			name: "role",
			label: "Role",
			error: "",
			option: uniqueRoleFilterOption,
		},
	];

	if (isSuperAdmin) {
		filterFields.push({
			inputType: "dropdown",
			id: "orgId",
			name: "orgId",
			label: "Organization",
			error: "",
			option: orgOptions,
		});
	}
	if (shouldHandlePlant) {
		filterFields.push({
			inputType: "dropdown",
			id: "plantIds",
			name: "plantIds",
			label: "Warehouse",
			error: "",
			option: plantOptions,
			value: filterRef.current?.plantIds,
		});
	}

	const [handleDelete] = useDeleteRoleMutation();
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
	const deleteDataRef = useRef(null);

	let actionHandlers = {};
	if (userPermissions?.updateUserRole?.value || isSuperAdmin) {
		actionHandlers.edit = (id) => {
			updateDataRef.current = roles[id];
			setIsModal(true);
		};
	}
	if (userPermissions?.deleteUserRole?.value || isSuperAdmin) {
		actionHandlers.delete = (id) => {
			deleteDataRef.current = roles[id];
			setDeleteConfirmOpen(true);
		};
	}
	const handleDeleteConfirm = async () => {
		if (!deleteDataRef.current) return;

		const payload = { id: deleteDataRef.current._id, orgId: deleteDataRef.current.orgId };
		const {
			data: { error, message },
		} = await handleDelete(payload);

		if (error) {
			toastMessage(false, message);
		} else {
			onUpdateSuccess();
			toastMessage(true, message);
		}
		setDeleteConfirmOpen(false);
		deleteDataRef.current = null;
	};

	const handleDeleteCancel = () => {
		setDeleteConfirmOpen(false);
		deleteDataRef.current = null;
	};
	let header = ["S.no", "Role", "Display Name"];
	if (isSuperAdmin) header.push("Org Name");
	if (userPermissions?.updateUserRole?.value || userPermissions?.deleteUserRole?.value || isSuperAdmin) {
		header.push("Action");
	}
	const rows =
		(shouldRunTour || isTourMode ? rolesTourDummyData : roles)?.map((d, i) => {
			let row = [];
			row.push(tCell((page_no - 1) * pagesize + i + 1), tCell(d.role), tCell(d.displayName));

			if (isSuperAdmin) row.push(tCell(d.orgName));

			if (userPermissions?.updateUserRole?.value || userPermissions?.deleteUserRole?.value || isSuperAdmin) {
				row.push(
					<div style={{ paddingLeft: "0.5rem" }}>
						<ActionMenu id={i} handlers={actionHandlers} />
					</div>
				);
			}
			return row;
		}) || [];

	const onPageChange = (page_no) => {
		filterRef.current = { ...filterRef.current, page_no: page_no + 1 };
		getRoles(filterRef.current);
	};
	const onPageSizeChange = (e) => {
		const { value } = e.target;
		filterRef.current = { ...filterRef.current, pagesize: value, page_no: 1 };
		getRoles(filterRef.current);
	};

	const onUpdateSuccess = () => {
		getRoles(filterRef.current);
		getRolesForFilter({});
		setFetchStats((old) => !old);
	};

	const handleFilterChange = (_filter) => {
		let filter = { ..._filter };

		for (let key in filter) {
			if (!filter[key]) delete filter[key];
		}
		if (isSuperAdmin) {
			if (filter.orgId) {
				// filter.orgId = filter.orgId?.value;
				if (shouldHandlePlant && filter.orgId != filterRef.current.orgId) {
					// when switching organization, we want to
					// - get plants for that org
					// - clear plantIds from the local filter object
					// - clear plantIds from original filter object, mutating the state and clearing the plant dropdown
					delete filter.plantIds;
					delete _filter.plantIds;
					getPlantDetails({ orgId: filter.orgId });
				}
			} else {
				clearPlants();
			}
		}
		// if (filter.plantIds) filter.plantIds = filter.plantIds?.value;

		filter.page_no = 1;
		filter.pagesize = 10;
		if (!isObjectEqual(filterRef.current, filter)) {
			filterRef.current = { ...filter };
			getRoles(filterRef.current);
		}
	};

	if (isFetched && !isSuperAdmin && userPermissions && Object.keys(userPermissions).length && !userPermissions.getUserRoles?.value) return <Navigate to="/not-authorized" replace />;

	return (
		<div className="contentpanel">
			<CommonTour ref={tourRef} steps={RolesTour} moduleName="roles" onTourComplete={() => setIsTourMode(false)} />
			<div className="cust-row flex-algn-cent">
				<div className="cust-col-5">
					<h1 className="title">
						Roles{" "}
						<span title="Replay Tour" onClick={handleTourStart} style={{ cursor: "pointer" }}>
							<TourIcon />
						</span>
					</h1>
				</div>
				{userPermissions?.createUserRole?.value || isSuperAdmin ? (
					<div className="cust-col-5 flex-jc-end">
						<Link className="a_tag_no">
							<Button onClick={onModalOpen} text="Add Role" style={buttonClasses.lynkitOrangeEmpty} />
						</Link>
					</div>
				) : null}
			</div>
			<hr className="bgg" />
			<GenericstatsV2 statsFor={"roles"} fetchStats={fetchStats} />
			<div className="cust-row flex-algn-cent">
				<Genericfilter filterFields={filterFields} onFilterChange={debounce((filter) => handleFilterChange(filter), 500)} addOnClear />
			</div>
			<div className="main_content">
				<GenericTable
					header={header}
					rows={rows}
					pageCount={count}
					pageNo={filterRef.current?.page_no}
					limit={filterRef.current?.pagesize}
					onPageChange={onPageChange}
					onPageSizeChange={onPageSizeChange}
					isLoading={isLoading}
				/>
			</div>

			{isModal && <AddRole open={isModal} onClose={onModalClose} updateData={updateDataRef.current} onUpdateSuccess={onUpdateSuccess} />}
			<ConfirmationDialog open={deleteConfirmOpen} handleClose={handleDeleteCancel} handleSubmit={handleDeleteConfirm} title="Delete Role" submitText="Delete">
				Are you sure you want to delete this role? This action cannot be undone.
			</ConfirmationDialog>
		</div>
	);
};

export default Roles;

const defaultForm = {
	role: "",
	displayName: "",
	updateId: undefined,
	orgId: undefined,
	orgName: "",
	plantIds: [],
	checkpoints: [],
	hierarchy: 0,
};

function fillUpdateData(data) {
	return {
		role: data.role,
		displayName: data.displayName,
		updateId: data._id,
		orgId: data.orgId,
		plantIds: data.plantIds,
		checkpoints: data.checkpoints || [],
		hierarchy: data.hierarchy,
	};
}

function AddRole({ onClose, open, updateData, onUpdateSuccess }) {
	const [formData, setFormData] = useState(updateData ? fillUpdateData(updateData) : defaultForm);
	const [error, setError] = useState({});
	const { userDetail, isSuperAdmin } = useUserDetail();
	const { userPermissions } = useUserPermissions();
	const { orgOptions } = useOrgs({}, { skip: !isSuperAdmin });
	const orgValue = orgOptions.find((_) => _.value == formData.orgId) || null;
	const { plantOptions, getPlantDetails, clearPlants } = usePlant(null, "addRolePlants", true);
	const plantValue = plantOptions.filter((_) => formData.plantIds.includes(_.value));
	const { checkpoints, getCheckpoints, clearCheckpointData } = useCheckpoints(null, "roleChkpts");
	const shouldHandlePlant = isSuperAdmin || userPermissions?.managePlant?.value;

	const map = new Map();
	checkpoints.forEach((checkpoint) => {
		const plantName = checkpoint.plantName;
		if (!map.has(plantName)) {
			map.set(plantName, []);
		}
		map.get(plantName).push({
			label: `${checkpoint.displayName} (${plantName})`,
			value: checkpoint._id,
			plantName: plantName,
		});
	});

	const checkpointOptions = Array.from(map.values()).flat();
	let checkpointValues = checkpointOptions?.filter((chk) => formData.checkpoints?.find((fchk) => fchk == chk.value));
	useEffect(() => {
		if (!userDetail) return;
		if (userDetail?.userRoleHierarchy != undefined && !updateData) {
			setFormData((o) => ({ ...o, hierarchy: userDetail.userRoleHierarchy == 0 ? userDetail.userRoleHierarchy : userDetail.userRoleHierarchy + 1, orgId: userDetail.orgId }));
		}
		if (userPermissions?.managePlant?.value && !isSuperAdmin) {
			getPlantDetails({});
		}
		if (!userPermissions?.managePlant?.value && !isSuperAdmin) {
			setFormData((o) => ({ ...o, orgId: userDetail?.orgId, plantIds: userDetail?.plantIds || [] }));
		}
	}, [userDetail]);
	useEffect(() => {
		if (formData.orgId) {
			getPlantDetails({ orgId: formData.orgId });
		} else {
			getPlantDetails({});
		}
	}, [formData.orgId]);

	useEffect(() => {
		if (formData.plantIds?.length) {
			getCheckpoints({ plantIds: formData.plantIds });
		} else {
			clearCheckpointData();
		}
	}, [formData.plantIds]);

	const handleInput = (e) => {
		const { name, value } = e.target;
		setFormData({ ...formData, [name]: value });
	};

	const onDropdownChange = (option, name) => {
		let value = Array.isArray(option) ? option.map((_) => _.value) : option?.value || "";
		if (name == "plantIds" && value?.includes("all")) {
			value = plantOptions.slice(1).map((_) => _.value);
		}
		handleInput({ target: { name, value } });
	};

	const [handleAdd, { isLoading: isAdding }] = useAddRoleMutation();
	const [handleUpdate, { isLoading: isUpdating }] = useUpdateRoleMutation();

	const handleFormSubmit = async () => {
		const payload = { ...formData, orgName: orgValue?.label };
		// console.log(payload);
		const {
			data: { error, message },
		} = updateData ? await handleUpdate(payload) : await handleAdd(payload);
		if (error) {
			toastMessage(false, message);
		} else {
			toastMessage(true, message);
			onUpdateSuccess();
			handleClose();
		}
	};
	// console.log(formData)
	const handleFormValidation = () => {
		let error = {};
		if (isSuperAdmin && !formData.orgId) error.orgId = "Organization is required";
		if (!formData.plantIds?.length) error.plantIds = "Warehouse is required";
		if (formData.hierarchy == undefined) error.hierarchy = "Role Hierarchy is required";
		if (!formData.role) error.role = "Role is required";
		if (!formData.displayName) error.displayName = "Display Name is required";

		if (Object.keys(error).length) {
			setError(error);
		} else {
			handleFormSubmit();
		}
	};
	const handleClose = () => {
		setFormData(defaultForm);
		onClose();
	};

	return (
		<Dialog size="xs" open={open} handleClose={handleClose} handleSubmit={handleFormValidation} title={updateData ? "Update Role" : "Add Role"} isSubmitDisabled={isAdding || isUpdating}>
			<form>
				<div className="form-inputs mt-2">
					{userDetail?.userType == "superAdmin" ? (
						<Dropdown id="orgId" name="orgId" label="Organization" onChange={onDropdownChange} options={orgOptions} value={orgValue} required error={error.orgId} />
					) : null}
					{shouldHandlePlant ? (
						<Dropdown id="plantIds" name="plantIds" label="Warehouse" onChange={onDropdownChange} options={plantOptions} value={plantValue} required multiple error={error.plantIds} />
					) : null}
					<Input
						label="Hierarchy"
						placeholder="Entry Hierarchy"
						name="hierarchy"
						value={formData.hierarchy}
						onChange={handleInput}
						required
						error={error.hierarchy}
						type="number"
						min={(userDetail.userRoleHierarchy || 0) + 1}
					/>
					<Input label="Role" placeholder="Enter Role" name="role" value={formData.role} onChange={handleInput} required error={error.role} />
					<Input label="Display Name" placeholder="Enter Display Name" name="displayName" value={formData.displayName} onChange={handleInput} required error={error.displayName} />

					<Dropdown
						id="checkpoints"
						name="checkpoints"
						label="Checkpoints"
						onChange={onDropdownChange}
						options={checkpointOptions}
						value={checkpointValues}
						error={error.checkpoints}
						multiple
						groupBy={(option) => option.plantName}
						renderTags={(value, getTagProps) =>
							value.map((option, index) => (
								<Chip
									{...getTagProps({ index })}
									key={option.value}
									label={option.compositeLabel}
									style={{
										margin: "2px",
										maxWidth: "200px",
										height: "auto",
									}}
								/>
							))
						}
						disableCloseOnSelect = {true}
					/>
				</div>
			</form>
		</Dialog>
	);
}
